<template>
  <v-autocomplete
    ref="searchBar"
    v-model="selected"
    :search-input.sync="searchText"
    class="repository-search"
    :items="localBooks && debouncedSearchTextValid ? localBooks.collection : []"
    prepend-inner-icon="search"
    :loading="loading"
    :allow-overflow="false"
    hide-spin-buttons
    flat
    hide-details
    solo
    dense
    outlined
    tile
    clearable
    :label="$t('common.search')"
    :hide-no-data="!debouncedSearchTextValid || loading"
    :light="true"
    small-chips
    :item-text="getItemText"
    item-value="id"
    no-filter
    @keyup.esc="clear"
    @keydown.enter="event => navigateToResults(event)"
  >
    <template #selection />
    <template #item="{ item }">
      <div class="d-flex align-center pa-2">
        <book-cover-image
          v-if="item.thumbnailUrl || item.imageUrl"
          :src="item.thumbnailUrl || item.imageUrl"
          :alt="$t('bookInfo.bookCoverAlt', { title: item.title })"
          class="mr-3 flex-shrink-0"
          height="40"
          width="30"
          contain
        />
        <div
          class="flex-grow-1"
          style="min-width: 0;"
        >
          <div class="font-weight-bold text-truncate">
            {{ item.title }}
          </div>
          <div 
            v-if="item.bookAuthors && item.bookAuthors.length" 
            class="text-caption text--secondary text-truncate"
          >
            {{ item.bookAuthors.map(ba => ba.author.name).join(', ') }}
          </div>
          <div 
            v-if="item.publisher?.name"
            class="text-caption text--secondary text-truncate" 
          >
            {{ item.publisher.name }}
          </div>
        </div>
      </div>
    </template>
    <template #no-data>
      <div 
        v-if="debouncedSearchTextValid && localBooks && localBooks.collection.length === 0"
        class="pa-4 text-center text--secondary"
      >
        <ekitabu-icon
          class="mb-2"
          size="48"
          color="grey lighten-1"
        >
          mdi-magnify-close
        </ekitabu-icon>
        <div class="text-subtitle-1">
          {{ $t('common.noResults') }}
        </div>
        <div class="text-caption">
          {{ $t('common.tryDifferentKeywords') }}
        </div>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
  import gql from "graphql-tag";
  import { debounce } from "debounce";
  import BookCoverImage from "@/components/BookCoverImage";

  export default {
    components: {
      BookCoverImage,
    },
    data() {
      return {
        searchText: "",
        debouncedSearchText: "",
        loading: false,
        selected: null,
      };
    },
    apollo: {
      localBooks: {
          query: gql` query searchProducts($page: Int! $searchText: String) {
            localBooks(page: $page, simplesearch: $searchText) {
              collection {
                id
                title
                description
                bookAuthors {
                  author {
                    name
                  }
                }
                thumbnailUrl
                imageUrl
                publisher {
                  name
                }
              }
            }
          }`,
          variables() {
            return {
              searchText: this.debouncedSearchText,
              page: 1,
            }
          },
          throttle: 500,
          skip() {
            if (this.debouncedSearchTextValid) {
                return false
            }
            return true
          },
        watchLoading(isLoading) {
          this.loading = isLoading
        },
      }
    },
    computed: {
      searchTextValid() {
        return this.searchText != null && this.searchText.length > 2;
      },
      debouncedSearchTextValid() {
        return this.debouncedSearchText != null && this.debouncedSearchText.length > 2;
      },
    },
    watch: {
      searchText(val) {
        this.debounceSearch(val);
      },
      selected(val) {
        this.$refs.searchBar.clearableCallback();
        if (val) {
          this.$router.push({ name: "book", params: { id: val } });
        }
      }
    },
    methods: {
      debounceSearch: debounce(function (val) {
        this.debouncedSearchText = val;
      }, 300),
      clear() {
        this.searchText = "";
        this.debouncedSearchText = "";
      },
      containsString(text) {
        if(this.debouncedSearchTextValid) {
          return !!(text || '').toLowerCase().includes(this.debouncedSearchText.toLowerCase());
        }
        
        return null;
      },
      getItemText(item) {
        return this.containsString(item.title) ? item.title : item.description;
      },
      navigateToResults() {
        if (this.searchText && this.$route?.params?.searchTerm != this.searchText) {
          this.$router.push({ name: "searchResults", params: { searchTerm: this.searchText } });
        }
        this.$refs.searchBar.isFocused = false;
      },
    },
  };
</script>

<style scoped>
  .repository-search.v-text-field {
    border-radius: 0px;
  }
</style>
